/**
 * 飲食ブランド線（Kalori for Brands・`brands.kalori.jp`）の対外の名前と、admin → merchant-api の管理契約。
 *
 * 身元は developer / trainer と同じ B2B 共通方式（`docs/architecture/b2b-onboarding.md` §8）:
 * 申請 → admin で承認 → **merchant-api が事業者と担当者を作り招待 URL を返す** → 招待 URL で有効化 →
 * 以後はメールの確認コードでログイン。自己登録の口は無い。
 *
 * ⚠️ 事業者・担当者を作るのは **biz-db の所有者（merchant-api）**。admin は biz-db を bind しているが、
 *    範囲は承認キューの 3 表（`scripts/plane-boundaries/policy.ts` の `BIZ_DB_TABLE_SCOPE`）で、
 *    `merchant_users` には触らない — developer（developer-api）・trainer（app-api）と同じく所有者へ委譲する。
 */
/** 対外の製品名（メール件名・画面）。内部識別子の `merchant` は外へ出さない（`docs/positioning/BUSINESS_LINES.md`）。 */
export const BRANDS_PRODUCT_NAME = "Kalori for Brands";
/**
 * 事業者の管理画面のホスト。API は同一オリジンの `/api/*`（merchant-api）。
 * ⚠️ ホストは対外名の側（coaches.kalori.jp と同じ規則）。公開前の 2026-10-03 に `business.kalori.jp` から変えた —
 *    `business` は LP の `kalori.jp/business/` が 3 線の総称に使っている語で、Coaches / Catalog API の利用者が
 *    自分のログイン先と取り違えるため。
 */
export const BRANDS_ORIGIN = "https://brands.kalori.jp";
/**
 * 利用申請の画面（ログイン不要・審査制）。LP の CTA はここへ送る（developer = console.kalori.jp/apply・
 * trainer = coaches.kalori.jp/apply と同じ形。2026-10-03 に LP のフォームから移した — 申請 → 承認 → 初回ログイン →
 * 利用を 1 つのホストで追うため・docs/architecture/b2b-onboarding.md §1）。
 */
export const BRANDS_APPLY_URL = `${BRANDS_ORIGIN}/apply`;
/**
 * 招待制の無料パイロットに参加したブランドの特典の長さ（月）。**最初の有料機能の提供開始日から**この月数は、
 * 有料の機能も無料（時計は 1 本・機能ごとに数え直さない）。2026-10-04 に置いた一旦の値。
 * 招待メール（admin `lib/merchant-invite.ts`）がここを読む — 数字を直書きしない。
 * （LP には 2026-10-06 から出していない = 「無料で掲載できます」とだけ言う。）線の正 = `cloudflare/workers/merchant-api/ROADMAP.md` §🎯。
 * ⚠️ 既に招待した相手への約束は、ここを変えても変わらない（短くするときは既存の参加ブランドを旧条件のまま扱う）。
 */
export const BRANDS_PILOT_FREE_MONTHS = 12;
/** 招待 URL（管理画面の `/invite/:token`。トークンは `randomToken()` の base64url 43 文字）。 */
export function brandsInviteUrl(token) {
    return `${BRANDS_ORIGIN}/invite/${token}`;
}
/** admin が送るメールに入れてよい招待 URL の形（他の面の URL や任意の URL を貼らせない）。 */
// ホストは literal で書かず BRANDS_ORIGIN から組む（付け替えたときに検証だけ旧ホストに残る事故を防ぐ・trainer-invite.ts と同じ）
export const BRANDS_INVITE_URL_RE = new RegExp(`^${BRANDS_ORIGIN.replace(/[.]/g, "\\.")}/invite/[A-Za-z0-9_-]{43}$`);
// ── 利用規約（飲食ブランド向け補遺） ──
/**
 * 公開中の「利用規約 — 飲食ブランド向け補遺」の版。**null = まだ公開していない**。
 *
 * 担当者はこの版に同意しないと公式値を申請できない（2026-10-03 オーナー判断）。申請した値は承認後に kalori の
 * カタログへ入り、Catalog API（`source: operator`）で外部の事業者へも提供されるため、その同意を申請の前提にする。
 * 同意した版は担当者（merchant_users.terms_version）と、申請・承認の履歴（merchant_entry_events.terms_version）に残る。
 *
 * ⚠️ null の間は誰も申請できない（fail-closed）。規約の本文を公開した commit で版を入れる。
 * ⚠️ 規約の文言を変えたら版を上げる（全員に再同意が出る）。誤字直しなら上げない — console の TERMS_VERSION と同じ規則。
 */
export const BRANDS_TERMS_VERSION = "2026-10-07";
/** 規約の本文（kalori.jp の法務ページ。coaches の補遺と同じ置き場）。 */
export const BRANDS_TERMS_URL = "https://kalori.jp/ja/terms/brands/";
// ── 値の根拠（由来の軸）・差し戻し理由 — DB の CHECK と対（migrations-biz/0011） ──
/** 公式値の根拠。申請の時に事業者にしか答えられない（後から聞き直せない）ので、申請の必須項目にする。 */
export const VALUE_BASES = {
    lab_analyzed: "分析機関での成分分析",
    manufacturer_spec: "メーカーの規格書・公表値",
    recipe_calculated: "レシピ（配合）から成分表で計算",
    estimated: "上記以外の推定",
};
export function isValueBasis(v) {
    return typeof v === "string" && Object.hasOwn(VALUE_BASES, v);
}
/** 運営が差し戻すときの理由（事業者の画面にそのまま出す・自由記述のメモを添えられる）。 */
export const MERCHANT_REJECT_REASONS = {
    value_check: "エネルギーと成分の値が合っていない可能性があります（桁・単位をご確認ください）",
    wrong_product: "紐付けたメニューが違う可能性があります",
    basis_unclear: "値の根拠を確認させてください",
    other: "その他（メモをご確認ください）",
};
export function isMerchantRejectReason(v) {
    return typeof v === "string" && Object.hasOwn(MERCHANT_REJECT_REASONS, v);
}
/**
 * 履歴（merchant_entry_events.snapshot_json）に残す形。申請（merchant-api）と承認（admin）が同じ関数で作る
 * — 形を 2 か所に持つと「申請した値」と「承認した値」を突き合わせられなくなる。
 */
export function entrySnapshot(row) {
    let allergens = null;
    try {
        allergens = row.allergens_json ? JSON.parse(row.allergens_json) : null;
    }
    catch {
        allergens = null;
    }
    return JSON.stringify({
        productDbId: row.product_db_id,
        nutrition: {
            cal: row.cal,
            protein: row.protein,
            fat: row.fat,
            carbs: row.carbs,
            sugar: row.sugar,
            fiber: row.fiber,
            salt: row.salt,
        },
        allergens,
        allergenStates: parseAllergenStates(row.allergen_states_json ?? null),
        servingGrams: row.serving_grams ?? null,
        valueBasis: row.value_basis,
        basisNote: row.basis_note,
        photoId: row.photo_id ?? null,
    });
}
// ── アレルゲン（4 状態・品目リストの版・2026-10-07）──
//
// ⚠️ **記録の道具まで**（判定はしない・ROADMAP §課金の対象「アレルゲンの誤りは人身事故」）。kalori は推定しない。
//    部品（自社の食材・部品に事業者が付けた状態）からの伝播も「候補」として見せるだけで、確定は事業者が押す。
/** 4 状態。「含まない」は事業者が確かめたときだけ（記載が無いことを「含まない」の根拠にしない — ROADMAP §PDF 取込の安全境界） */
export const ALLERGEN_STATES = {
    contains: "含む",
    may_contain: "コンタミの可能性",
    not_contained: "含まない",
    unknown: "未確認",
};
export function isAllergenState(v) {
    return typeof v === "string" && Object.hasOwn(ALLERGEN_STATES, v);
}
/**
 * 品目リストの版。⚠️ 法改正（消費者庁の通知）で品目が変わる。変えたら版を上げ、古い版で記録した行はその版のまま読む
 * （記録の時点で何を確かめたかを書き換えない）。⚠️ 版を上げる前に、運営が消費者庁の最新の通知と照合する。
 */
export const ALLERGEN_LIST_VERSION = "2024-03";
/** 特定原材料（表示の義務がある品目） */
export const ALLERGEN_MANDATORY = ["えび", "かに", "くるみ", "小麦", "そば", "卵", "乳", "落花生"];
/** 特定原材料に準ずるもの（表示を推奨する品目） */
export const ALLERGEN_RECOMMENDED = [
    "アーモンド", "あわび", "いか", "いくら", "オレンジ", "カシューナッツ", "キウイフルーツ", "牛肉", "ごま", "さけ",
    "さば", "大豆", "鶏肉", "バナナ", "豚肉", "マカダミアナッツ", "もも", "やまいも", "りんご", "ゼラチン",
];
export function parseAllergenStates(json) {
    if (!json)
        return null;
    try {
        const v = JSON.parse(json);
        if (!v || typeof v.items !== "object" || v.items == null)
            return null;
        const items = {};
        for (const [k, s] of Object.entries(v.items))
            if (isAllergenState(s))
                items[k] = s;
        return { version: typeof v.version === "string" ? v.version : ALLERGEN_LIST_VERSION, items };
    }
    catch {
        return null;
    }
}
/**
 * 「含む」の品目名（カタログの射影 `allergens_json` に書く形・以前からの互換）。
 * ⚠️ コンタミの可能性は入れない — 射影の `allergens_json` は「事業者が含むと確認したもの」の意味で読まれている。
 */
export function containedAllergens(states) {
    if (!states)
        return [];
    return Object.entries(states.items)
        .filter(([, s]) => s === "contains")
        .map(([k]) => k);
}
/**
 * 部品・自社食材に付いた状態をメニューの「候補」にまとめる（強い方を残す: 含む > コンタミの可能性）。
 * 「含まない」「未確認」は伝えない（部品が含まなくても、メニューの他の材料が含みうる）。
 */
export function allergenCandidates(sources) {
    const out = {};
    for (const s of sources) {
        if (!s)
            continue;
        for (const [k, v] of Object.entries(s.items)) {
            if (v === "contains")
                out[k] = "contains";
            else if (v === "may_contain" && out[k] !== "contains")
                out[k] = "may_contain";
        }
    }
    return out;
}
export function valueCheckWarnings(v) {
    const out = [];
    if (v.carbs != null && (v.sugar != null || v.fiber != null) && (v.sugar ?? 0) + (v.fiber ?? 0) > v.carbs + 0.5) {
        out.push("sugar_fiber_exceed_carbs");
    }
    if (v.servingGrams != null && v.servingGrams > 0) {
        const mass = (v.protein ?? 0) + (v.fat ?? 0) + (v.carbs ?? 0) + (v.salt ?? 0);
        if (mass > v.servingGrams + 0.5)
            out.push("mass_exceeds_serving");
        if (v.cal != null && (v.cal / v.servingGrams) * 100 > 900)
            out.push("energy_density_impossible");
    }
    if (v.salt != null && v.salt > 15)
        out.push("salt_very_high");
    return out;
}
export const VALUE_CHECK_LABEL = {
    sugar_fiber_exceed_carbs: "糖質と食物繊維の合計が、炭水化物を超えています（炭水化物 = 糖質 + 食物繊維）。",
    mass_exceeds_serving: "たんぱく質・脂質・炭水化物・食塩の合計が、1 食の重量を超えています。単位（g / mg）や 100g あたりとの取り違えをご確認ください。",
    energy_density_impossible: "100g あたり 900 kcal を超えています（油だけでも約 900 kcal）。エネルギーか 1 食の重量をご確認ください。",
    salt_very_high: "食塩相当量が 1 食 15g を超えています。mg と g、100g あたりと 1 食あたりの取り違えをご確認ください。",
};
/** 紐付け先を読むときの列。⚠️ 判定に使う列を増やしたら、ここと {@link LinkedProductRow} を一緒に直す */
export const LINKED_PRODUCT_COLUMNS = "state, app_visible, lifecycle_state, deleted_date";
/**
 * 公式値の紐付け先がいまカタログに載っているかを判定する。admin の公開中一覧・承認と、
 * 事業者の台帳（merchant-api）で同じ判定を使う（**両方の画面で言うことを食い違わせない**）。
 *
 * ⚠️ 公開中の上書きは、紐付け先が外れると**黙って誰にも見えなくなる**（ROADMAP §設計判断 4「override の孤児化」）。
 * チェーンがメニュー名を変えると夜間 merge は新しい行を作り、古い行は猶予を経て state=0 へ落ちる —
 * 公式値は古い行に付いたままで、新しい行は AI 推定に戻る。merge は「スクレイプに一度も出ていない商品」しか
 * 上書きで守らない（`computeAbsentProductTransition` の operator_owned）ので、ここで拾うしかない。
 */
export function linkedProductState(p) {
    if (!p)
        return "gone";
    if (p.state !== 1 || p.app_visible !== 1 || p.lifecycle_state !== "active")
        return "gone";
    if (p.deleted_date != null)
        return "leaving";
    return "listed";
}
// ── 管理画面からのお問い合わせ（スレッド・2026-10-05）──
// 置き場 = biz-db merchant_feedback / merchant_feedback_messages（migrations-biz/0012）。メールではなく管理画面の中で往復する。
// 書き手は merchant-api だけ（事業者の発言 = 公開面・運営の返信と状態 = AdminManagementV1 の feedback_reply / feedback_status）。
// 読み書きの規則は B2B の各コンソールと同じ共通部品（`@kalori/business/support` の line "merchant"・2026-10-16〜）。
/**
 * 何についての声か（画面で最初に選ぶ）。並びは画面の順。
 * ⚠️ 「ほしい機能」はパイロットの終わりに有料の最初の 1 つを決める材料（ROADMAP §🎯）— 受信箱で数えられるよう分けて持つ。
 */
export const BRAND_FEEDBACK_CATEGORIES = {
    listing: "掲載内容の誤り",
    feature: "ほしい機能",
    trouble: "使いにくい・うまく動かない",
    question: "質問",
    other: "その他",
};
/** 1 回の発言の上限（運営の返信も同じ） */
export const BRAND_FEEDBACK_MAX_LENGTH = 2000;
// ── admin → merchant-api（service binding `AdminManagementV1`）──
export const MERCHANT_ADMIN_PATH = "/internal/merchants";
/**
 * 商品写真の大きさ（カタログの AI 生成画像と同じ 3 サイズ・正方形の WebP）。suffix はキーの `_400` 等で、
 * 公開バケットでは `products/{shop}/generated/g/{generationId}{suffix}.webp`（`@kalori/catalog/images`）になる。
 */
export const MERCHANT_PHOTO_VARIANTS = [
    { suffix: "", size: 512 },
    { suffix: "_400", size: 400 },
    { suffix: "_200", size: 200 },
];
export async function manageMerchant(binding, input) {
    const response = await binding.fetch(new Request(`https://merchant.internal${MERCHANT_ADMIN_PATH}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
    }));
    if (response.status >= 500)
        throw new Error("Merchant management unavailable");
    const result = (await response.json());
    if (typeof result.success !== "boolean")
        throw new Error("Invalid merchant management response");
    return result;
}
//# sourceMappingURL=brands.js.map